import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = '5 June 2026';
const CONTROLLER = 'SeaCloud Software d.o.o.';
const ADDRESS = 'Ulica Milana Pavelića 1B, 10000 Zagreb, Croatia';
const OIB = '01433662496';
const EMAIL = 'info@seacloud-software.hr';

export default function Privacy() {
  useEffect(() => {
    document.title = 'Privacy Policy — SeaCloud Software';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-inner">
        <Link to="/" className="legal-back">← Back to home</Link>

        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-subtitle">Last updated: {LAST_UPDATED}</p>

        <div className="privacy-prose">

          <h2>1. Who we are (Data Controller)</h2>
          <p>
            {CONTROLLER}, {ADDRESS}, OIB: {OIB}.<br />
            Contact: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
          </p>

          <h2>2. What data we collect and why</h2>

          <h3>Server / access logs</h3>
          <p>
            When you visit this website, our hosting provider (Microsoft Azure Static Web Apps) automatically
            records standard access log data: your IP address, browser type, referring URL, and the pages
            you requested. This is a technical necessity for operating any website.
          </p>
          <ul>
            <li><strong>Legal basis:</strong> Legitimate interest (Art. 6(1)(f) GDPR) — ensuring server security and diagnosing technical issues.</li>
            <li><strong>Retention:</strong> Azure retains access logs for up to 30 days. We do not export or separately store these logs.</li>
          </ul>

          <h3>Fonts</h3>
          <p>
            This website serves all fonts directly from our own hosting infrastructure. No request is made
            to Google Fonts, Adobe Fonts, or any other third-party font service. Your browser does not
            contact any external server to load fonts.
          </p>

          <h3>Email contact</h3>
          <p>
            If you contact us by email at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>, we will receive your
            email address and the contents of your message. We use this solely to respond to your enquiry.
          </p>
          <ul>
            <li><strong>Legal basis:</strong> Legitimate interest / pre-contractual steps (Art. 6(1)(b) and (f) GDPR).</li>
            <li><strong>Retention:</strong> We keep email correspondence for as long as it is commercially relevant, typically no longer than 3 years.</li>
            <li><strong>Processor:</strong> Email is handled via Google Workspace. Google processes data as a data processor under a Data Processing Agreement with us.</li>
          </ul>

          <h2>3. Cookies</h2>
          <p>
            This website uses <strong>no tracking or advertising cookies</strong>. The only cookie-like
            storage we use is a single <code>localStorage</code> entry that records whether you have
            dismissed our cookie notice. This entry contains no personal data and never leaves your device.
          </p>
          <p>
            Because we set no cookies that require consent under the ePrivacy Directive, no cookie consent
            is legally required. Our cookie notice is informational only.
          </p>

          <h2>4. Third-party services</h2>
          <table className="privacy-table">
            <thead>
              <tr><th>Service</th><th>Purpose</th><th>Data sent</th><th>Privacy policy</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Microsoft Azure</td>
                <td>Website hosting</td>
                <td>IP address, access logs</td>
                <td><a href="https://privacy.microsoft.com" target="_blank" rel="noopener noreferrer">privacy.microsoft.com</a></td>
              </tr>
              <tr>
                <td>Google Workspace</td>
                <td>Email (inbound enquiries only)</td>
                <td>Email address, message content</td>
                <td><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com</a></td>
              </tr>
            </tbody>
          </table>
          <p>
            We do not use Google Analytics, Meta Pixel, LinkedIn Insight Tag, or any other behavioural
            tracking or advertising technology.
          </p>

          <h2>5. International transfers</h2>
          <p>
            Microsoft Azure and Google Workspace may process data in data centres outside the EU/EEA.
            Both providers offer Standard Contractual Clauses (SCCs) and are certified under the
            EU–US Data Privacy Framework, providing adequate safeguards under GDPR Art. 46.
          </p>

          <h2>6. Your rights</h2>
          <p>Under GDPR you have the right to:</p>
          <ul>
            <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
            <li><strong>Rectification</strong> — ask us to correct inaccurate data.</li>
            <li><strong>Erasure</strong> — ask us to delete your data where there is no compelling reason to continue processing it.</li>
            <li><strong>Restriction</strong> — ask us to restrict processing in certain circumstances.</li>
            <li><strong>Objection</strong> — object to processing based on legitimate interests.</li>
            <li><strong>Portability</strong> — receive your data in a structured, machine-readable format.</li>
          </ul>
          <p>
            To exercise any of these rights, email us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
            We will respond within 30 days.
          </p>
          <p>
            You also have the right to lodge a complaint with the Croatian Data Protection Authority
            (Agencija za zaštitu osobnih podataka — <a href="https://azop.hr" target="_blank" rel="noopener noreferrer">azop.hr</a>).
          </p>

          <h2>7. Data security</h2>
          <p>
            We take appropriate technical and organisational measures to protect your data. Our website
            is served exclusively over HTTPS. Email is handled through Google Workspace with two-factor
            authentication enforced.
          </p>

          <h2>8. Children</h2>
          <p>
            This website is not directed at children under 16. We do not knowingly collect data from children.
          </p>

          <h2>9. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. The "Last updated" date at the top of this page
            will reflect any changes. For significant changes we will post a notice on the homepage.
          </p>

          <h2>10. Contact</h2>
          <p>
            For any privacy-related questions, contact us at:<br />
            <strong>{CONTROLLER}</strong><br />
            {ADDRESS}<br />
            <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
          </p>

        </div>

        <p className="legal-footer-note" style={{ marginTop: '48px' }}>
          © {new Date().getFullYear()} SeaCloud Software d.o.o. All rights reserved.
        </p>
      </div>
    </div>
  );
}
